import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../atoms/Button';
import './RegistrationEncouragement.scss';
import Spacer from '@/components/atoms/Spacer';

interface RegistrationEncouragementProps {
  variant?: 'compact' | 'full';
  showSkip?: boolean;
  onSkip?: () => void;
  onSignIn?: () => void;
  onSignUp?: () => void;
}

const RegistrationEncouragement: React.FC<RegistrationEncouragementProps> = ({
  variant = 'compact',
  showSkip = true,
  onSkip,
  onSignIn,
  onSignUp
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Don't show registration encouragement if user is already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleSignUp = () => {
    if (onSignUp) {
      onSignUp();
      return;
    }
    navigate('/register');
  };

  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn();
      return;
    }
    navigate('/login');
  };

  if (variant === 'compact') {
    return (
      <div className="registration-encouragement registration-encouragement--compact">
        <div className="registration-encouragement__header">
          <h3 className="registration-encouragement__title registration-encouragement__title--compact">
            {t('anonymous.encouragement.title')}
          </h3>
          {showSkip && (
            <Button
              variant="ghost"
              size="xs"
              className="registration-encouragement__close"
              onClick={onSkip}
              aria-label={t('common.close')}
            >
              &times;
            </Button>
          )}
        </div>
        <Spacer size="1px" className="registration-encouragement__divider" />
        <div className="registration-encouragement__content">
          <p className="registration-encouragement__text">
            {t('anonymous.encouragement.compact')}
          </p>
          <div className="registration-encouragement__actions registration-encouragement__actions--compact">
            <Button
              variant="primary"
              size="sm"
              fullWidth
              className="registration-encouragement__primary-action"
              onClick={handleSignIn}
            >
              {t('anonymous.signIn')}
            </Button>
            <button
              type="button"
              className="registration-encouragement__link"
              onClick={handleSignUp}
            >
              {t('anonymous.signUp')}
            </button>
          </div>
        </div>
        
      </div>
    );
  }

  return (
    <div className="registration-encouragement registration-encouragement--full">
      <div className="registration-encouragement__card">
        <div className="registration-encouragement__icon">
          👤
        </div>
        <h3 className="registration-encouragement__title">
          {t('anonymous.encouragement.title')}
        </h3>
        <p className="registration-encouragement__description">
          {t('anonymous.encouragement.description')}
        </p>
        <div className="registration-encouragement__benefits">
          <div className="registration-encouragement__benefit">
            <span className="registration-encouragement__benefit-icon">📱</span>
            <span>{t('anonymous.encouragement.benefit1')}</span>
          </div>
          <div className="registration-encouragement__benefit">
            <span className="registration-encouragement__benefit-icon">💳</span>
            <span>{t('anonymous.encouragement.benefit2')}</span>
          </div>
          <div className="registration-encouragement__benefit">
            <span className="registration-encouragement__benefit-icon">🎁</span>
            <span>{t('anonymous.encouragement.benefit3')}</span>
          </div>
        </div>
        <div className="registration-encouragement__actions">
          <Button 
            variant="primary" 
            size="md"
            onClick={handleSignUp}
          >
            {t('anonymous.createAccount')}
          </Button>
          <Button 
            variant="outline" 
            size="md"
            onClick={handleSignIn}
          >
            {t('anonymous.alreadyHaveAccount')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationEncouragement; 